import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, of, switchMap } from 'rxjs';
import { WebshopSessionService } from '../services/webshop-session.service';
import { environment } from '../../../environments/environment';

function getAuthTokenFromCookie(): string | undefined {
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith('X-Auth-Token='))
    ?.split('=')[1];
}

/**
 * Staff identity rides on an `X-Auth-Token` cookie set by the ERP with `sameSite: 'lax'`, so it
 * never leaves the ERP's own domain — a dev server on localhost cannot see it in `document.cookie`
 * no matter which port or proxy it uses, and every internal feature reads as anonymous. (The
 * *webshop* session cookie is `sameSite: 'none'` on staging, which is why customer-facing flows
 * work locally and staff ones do not.)
 *
 * The backend accepts the same token as a header, so a developer can supply theirs by hand once,
 * from the browser console:
 *
 *   localStorage.setItem('imtDevAuthToken', '<X-Auth-Token cookie value from the ERP>')
 *
 * This is real authentication, not a mock: the backend still validates the token and derives the
 * roles from it, so endpoints like `travelSteps` behave exactly as they will in production.
 * Never active in a production build, and the token lives only in the developer's own browser.
 */
function getDevAuthToken(): string | undefined {
  if (environment.production) return undefined;
  try {
    return localStorage.getItem('imtDevAuthToken') ?? undefined;
  } catch {
    return undefined;
  }
}

function cloneWithAuth(req: Parameters<HttpInterceptorFn>[0]): ReturnType<typeof req.clone> {
  const token = getAuthTokenFromCookie() ?? getDevAuthToken();
  return req.clone({
    withCredentials: true,
    setHeaders: token ? { 'X-Auth-Token': token } : undefined,
  });
}

export const erpAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const isErpRequest = environment.erpBaseUrl
    ? req.url.startsWith(environment.erpBaseUrl)
    : req.url.startsWith('/api');
  if (isErpRequest) {
    const webshopSessionService = inject(WebshopSessionService);

    return next(cloneWithAuth(req)).pipe(
      switchMap((event) => {
        if (event instanceof HttpResponse && event.status === 202) {
          // 202 means we are missing a webshop session. We need to create one and retry.
          return from(webshopSessionService.createWebshopSession()).pipe(
            switchMap(() => next(cloneWithAuth(req))),
          );
        }
        return of(event);
      }),
    );
  }

  return next(req);
};
